import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { authAPI, publicAPI } from '../services/api';

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  permissions?: string[];
  /** Merchant: blocked from new payouts (can still log in). */
  payout_blacklisted?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<any>;
  register: (data: any) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionTimeoutMinutes, setSessionTimeoutMinutes] = useState<number | null>(null);

  useEffect(() => {
    const init = async () => {
      // Autologin handoff for newly opened tab (fallback mode)
      try {
        const params = new URLSearchParams(window.location.search);
        if (params.get('autologin') === '1') {
          const handoffToken = localStorage.getItem('autologin_token');
          const handoffUser = localStorage.getItem('autologin_user');
          if (handoffToken && handoffUser) {
            sessionStorage.setItem('token', handoffToken);
            sessionStorage.setItem('user', handoffUser);
            localStorage.removeItem('autologin_token');
            localStorage.removeItem('autologin_user');
            const cleanUrl = `${window.location.origin}${window.location.pathname}`;
            window.history.replaceState({}, document.title, cleanUrl);
          }
        }
      } catch {
        // no-op
      }

      // Check for stored token on mount
      const storedToken = sessionStorage.getItem('token') || localStorage.getItem('token');
      const storedUser = sessionStorage.getItem('user') || localStorage.getItem('user');

      if (storedToken && storedUser) {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
      }

      // Load security settings (session timeout) from public maintenance-status API
      try {
        const res = await publicAPI.getMaintenanceStatus();
        const minutes =
          res?.data?.data?.security?.session_timeout_minutes ??
          res?.data?.data?.security?.sessionTimeoutMinutes;
        const parsed = parseInt(String(minutes ?? ''), 10);
        if (!isNaN(parsed) && parsed > 0) {
          setSessionTimeoutMinutes(parsed);
        } else {
          setSessionTimeoutMinutes(10);
        }
      } catch (e) {
        // Fallback to 10 minutes if settings cannot be loaded
        setSessionTimeoutMinutes(10);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, []);

  const refreshProfile = useCallback(async () => {
    try {
      const t = localStorage.getItem('token');
      if (!t) return;
      const res = await authAPI.getProfile();
      const d = res.data?.data;
      if (!d) return;
      setUser(prev => {
        if (!prev) return prev;
        const next: User = {
          ...prev,
          payout_blacklisted: Boolean(d.payout_blacklisted),
        };
        localStorage.setItem('user', JSON.stringify(next));
        return next;
      });
    } catch {
      /* ignore */
    }
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await authAPI.login(email, password);
      const { token: newToken, user: newUser } = response.data.data;
      
      setToken(newToken);
      setUser(newUser);
      
      sessionStorage.setItem('token', newToken);
      sessionStorage.setItem('user', JSON.stringify(newUser));
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(newUser));
      
      return response; // Return the response for role-based redirection
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  };

  const register = async (data: any) => {
    try {
      const response = await authAPI.register(data);
      const { token: newToken, user: newUser } = response.data.data;
      
      setToken(newToken);
      setUser(newUser);
      
      sessionStorage.setItem('token', newToken);
      sessionStorage.setItem('user', JSON.stringify(newUser));
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(newUser));
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Registration failed');
    }
  };

  const logout = async () => {
    try {
      // Call logout API endpoint if needed
      if (token || sessionStorage.getItem('token') || localStorage.getItem('token')) {
        try {
          await authAPI.logout();
        } catch (error) {
          console.log('Logout API call failed, but continuing with local logout');
        }
      }
    } catch (error) {
      console.log('Error during logout:', error);
    } finally {
      // Always clear local state regardless of API call result
      setToken(null);
      setUser(null);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      
      // Clear any other cached data
      localStorage.clear();
      sessionStorage.clear();
    }
  };

  // Idle auto-logout based on sessionTimeoutMinutes from settings
  useEffect(() => {
    if (!token || !sessionTimeoutMinutes || sessionTimeoutMinutes <= 0) {
      return;
    }

    let timeoutId: number | undefined;
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];

    const resetTimer = () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      timeoutId = window.setTimeout(async () => {
        // Auto-logout only if still authenticated
        if (sessionStorage.getItem('token') || localStorage.getItem('token')) {
          await logout();
          try {
            // Optional reason parameter for UX
            window.location.href = '/login?reason=timeout';
          } catch {
            window.location.href = '/login';
          }
        }
      }, sessionTimeoutMinutes * 60 * 1000);
    };

    events.forEach((ev) => window.addEventListener(ev, resetTimer));
    resetTimer();

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      events.forEach((ev) => window.removeEventListener(ev, resetTimer));
    };
  }, [token, sessionTimeoutMinutes]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        register,
        logout,
        refreshProfile,
        isLoading,
        isAuthenticated: !!token,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

